import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Row, Col, Card, CardBody, Alert, Button, Spinner,
  FormGroup, Label, Input, Form
} from 'reactstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { config } from '../../config/environment';
import LoadingState from '../../Components/LoadingState';

const validationSchema = Yup.object({
  name: Yup.string().required('Organization name is required'),
  subdomain: Yup.string()
    .required('Subdomain is required')
    .matches(/^[a-z0-9-]+$/, 'Subdomain can only contain lowercase letters, numbers, and hyphens'),
  timezone: Yup.string().required('Timezone is required'),
  address: Yup.string().required('Address is required'),
  phone: Yup.string().required('Phone number is required'),
  email: Yup.string().email('Invalid email').required('Email is required'),
  gst: Yup.string().optional(),
});

interface TimezoneOption {
  value: string;
  label: string;
  currentTime: string;
  sortKey: number;
}

const getTimezoneOptions = (): TimezoneOption[] => {
  try {
    const timezones = (Intl as any).supportedValuesOf ?
      (Intl as any).supportedValuesOf("timeZone") :
      [
        'UTC', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
        'America/Toronto', 'America/Vancouver', 'America/Sao_Paulo', 'America/Mexico_City',
        'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Rome', 'Europe/Madrid',
        'Europe/Amsterdam', 'Europe/Stockholm', 'Europe/Zurich', 'Europe/Moscow',
        'Asia/Tokyo', 'Asia/Shanghai', 'Asia/Kolkata', 'Asia/Singapore', 'Asia/Dubai',
        'Asia/Seoul', 'Asia/Bangkok', 'Asia/Hong_Kong', 'Asia/Manila', 'Asia/Jakarta',
        'Australia/Sydney', 'Australia/Melbourne', 'Australia/Perth', 'Pacific/Auckland',
        'Africa/Cairo', 'Africa/Johannesburg', 'Africa/Lagos', 'Africa/Nairobi'
      ];

    return timezones.map((timezone: string) => {
      try {
        const now = new Date();
        const currentTime = now.toLocaleTimeString("en-US", {
          timeZone: timezone,
          hour12: true,
          hour: "numeric",
          minute: "2-digit",
        });

        const formatter = new Intl.DateTimeFormat('en', {
          timeZone: timezone,
          timeZoneName: 'longOffset'
        });
        const offsetString = formatter.formatToParts(now).find(part => part.type === 'timeZoneName')?.value || 'GMT+00:00';
        const gmtMatch = offsetString.match(/GMT([+-])(\d{2}):(\d{2})/);
        const gmtOffset = gmtMatch ? `GMT${gmtMatch[1]}${gmtMatch[2]}:${gmtMatch[3]}` : 'GMT+00:00';
        const tzOffsetMinutes = gmtMatch ?
          (gmtMatch[1] === '+' ? 1 : -1) * (parseInt(gmtMatch[2]) * 60 + parseInt(gmtMatch[3])) : 0;

        const displayName = timezone.replace(/_/g, ' ').split('/').pop();
        const region = timezone.split('/')[0];
        const fullDisplayName = region === timezone ? timezone : `${displayName} (${region})`;

        return {
          value: timezone,
          label: `(${gmtOffset}) ${fullDisplayName}`,
          currentTime: currentTime,
          sortKey: tzOffsetMinutes
        };
      } catch (error) {
        return {
          value: timezone,
          label: timezone,
          currentTime: '',
          sortKey: 0
        };
      }
    })
    .filter((tz: TimezoneOption) => tz.label)
    .sort((a: TimezoneOption, b: TimezoneOption) => a.sortKey - b.sortKey);
  } catch (error) {
    return [
      { value: 'UTC', label: '(GMT+00:00) UTC', currentTime: '', sortKey: 0 },
      { value: 'America/New_York', label: '(GMT-05:00) New York (America)', currentTime: '', sortKey: -300 },
      { value: 'Europe/London', label: '(GMT+00:00) London (Europe)', currentTime: '', sortKey: 0 },
      { value: 'Asia/Kolkata', label: '(GMT+05:30) Kolkata (Asia)', currentTime: '', sortKey: 330 },
    ];
  }
};

const CreateOrganization: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [flowData, setFlowData] = useState<any>(null);
  const [subdomainStatus, setSubdomainStatus] = useState<'idle' | 'checking' | 'available' | 'unavailable'>('idle');
  const [subdomainSuggestion, setSubdomainSuggestion] = useState('');
  const [timezoneOptions] = useState<TimezoneOption[]>(() => getTimezoneOptions());
  const [timezoneFilter, setTimezoneFilter] = useState('');
  const [showGst, setShowGst] = useState(false);

  const filteredTimezones = useMemo(() => {
    if (!timezoneFilter) return timezoneOptions;
    const query = timezoneFilter.toLowerCase();
    return timezoneOptions.filter(tz => tz.label.toLowerCase().includes(query));
  }, [timezoneOptions, timezoneFilter]);

  const checkOnboardingStatus = useCallback(async (onboardingFlowId: string) => {
    try {
      const response = await fetch(config.api.onboardingFlowById(onboardingFlowId), {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        const flow = await response.json();
        setFlowData(flow);

        if (flow.status !== 'PAID') {
          if (flow.status === 'CONFIGURING_STRUCTURE') {
            navigate(`/configure-structure?flow=${onboardingFlowId}`);
          } else if (flow.status === 'COMPLETE') {
            navigate(`/complete?flow=${onboardingFlowId}`);
          } else {
            navigate(`/getting-started?flow=${onboardingFlowId}`);
          }
          return;
        }
      } else {
        navigate(`/getting-started?flow=${onboardingFlowId}`);
      }
    } catch (error) {
      navigate(`/getting-started?flow=${onboardingFlowId}`);
    } finally {
      setPageLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    const flowId = searchParams.get('flow') || searchParams.get('session') || searchParams.get('flowId');
    let onboardingFlowId = flowId || localStorage.getItem('onboardingFlowId');

    if (flowId) {
      localStorage.setItem('onboardingFlowId', flowId);
      onboardingFlowId = flowId;
    }

    if (!onboardingFlowId) {
      navigate('/getting-started');
      return;
    }

    checkOnboardingStatus(onboardingFlowId);
  }, [navigate, searchParams, checkOnboardingStatus]);

  // Auto-validate subdomain on blur (debounced)
  const validateSubdomain = useCallback(async (subdomain: string) => {
    if (!subdomain || !/^[a-z0-9-]+$/.test(subdomain)) {
      setSubdomainStatus('idle');
      return;
    }

    setSubdomainStatus('checking');
    try {
      const response = await fetch(config.api.validateSubdomain(subdomain), {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      const result = await response.json();
      if (result.available) {
        setSubdomainStatus('available');
        setSubdomainSuggestion('');
      } else {
        setSubdomainStatus('unavailable');
        setSubdomainSuggestion(result.suggestion || '');
      }
    } catch (error) {
      setSubdomainStatus('idle');
    }
  }, []);

  const formik = useFormik({
    initialValues: {
      name: '',
      subdomain: '',
      address: '',
      phone: '',
      email: '',
      gst: '',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      setError('');
      setSuccess('');

      try {
        const onboardingFlowId = localStorage.getItem('onboardingFlowId');

        if (!onboardingFlowId) {
          setError('No onboarding flow found. Please start over.');
          return;
        }

        // Validate subdomain if not already validated
        if (subdomainStatus !== 'available') {
          const subdomainResponse = await fetch(config.api.validateSubdomain(values.subdomain), {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
          });

          const subdomainResult = await subdomainResponse.json();
          if (!subdomainResult.available) {
            setError(`Subdomain "${values.subdomain}" is not available. Try: ${subdomainResult.suggestion}`);
            return;
          }
        }

        const orgResponse = await fetch(config.api.orgSetup(onboardingFlowId), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: values.name,
            subdomain: values.subdomain,
            address: values.address,
            phone: values.phone,
            email: values.email,
            gst: values.gst || undefined,
            timezone: values.timezone,
          }),
        });

        if (orgResponse.ok) {
          setSuccess('Organization created successfully! Proceeding to structure configuration...');
          setTimeout(() => {
            const currentFlowId = localStorage.getItem('onboardingFlowId');
            const navigateUrl = currentFlowId ? `/configure-structure?flow=${currentFlowId}` : '/configure-structure';
            navigate(navigateUrl);
          }, 2000);
        } else {
          const errorData = await orgResponse.json();
          setError(errorData.message || 'Failed to create organization');
        }
      } catch (error) {
        setError('Something went wrong. Please try again.');
      } finally {
        setLoading(false);
      }
    },
  });

  // Pre-fill from flow data
  useEffect(() => {
    if (flowData?.organizationName) {
      if (!formik.values.name) {
        formik.setFieldValue('name', flowData.organizationName);
      }

      if (!formik.values.subdomain) {
        const subdomain = flowData.organizationName
          .toLowerCase()
          .replace(/[^a-z0-9\s]/g, '')
          .replace(/\s+/g, '-')
          .substring(0, 30);

        if (subdomain) {
          formik.setFieldValue('subdomain', subdomain);
        }
      }

      // Pre-fill email from lead's email
      if (!formik.values.email && flowData.email) {
        formik.setFieldValue('email', flowData.email);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowData]);

  // Auto-validate subdomain on change (debounced)
  useEffect(() => {
    if (!formik.values.subdomain) {
      setSubdomainStatus('idle');
      return;
    }

    const timeoutId = setTimeout(() => {
      validateSubdomain(formik.values.subdomain);
    }, 600);

    return () => clearTimeout(timeoutId);
  }, [formik.values.subdomain, validateSubdomain]);

  if (pageLoading) {
    return <LoadingState message="Loading organization setup..." />;
  }

  return (
    <div className="container py-4">
      <div className="row justify-content-center">
        <div className="col-12 col-md-10 col-lg-8 col-xl-7">
          <div className="text-center mb-4">
            <div className="mb-3">
              <div className="onboarding-icon">
                <i className="fa-solid fa-building" />
              </div>
            </div>
            <h4 className="mb-2 fw-bold">Create Your Organization</h4>
            <p className="text-muted mb-0">
              Enter your organization information to create your Mentorgain workspace.
            </p>
          </div>

          {error && <Alert color="danger">{error}</Alert>}
          {success && <Alert color="success">{success}</Alert>}

          <div className="onboarding-card-wrapper">
            <Card className="border-0">
              <CardBody className="p-4 p-md-5">
                <Form onSubmit={formik.handleSubmit}>
                  {/* Section: Organization Identity */}
                  <div className="form-section">
                    <div className="form-section-title">
                      <i className="fa-solid fa-building" />
                      Organization Identity
                    </div>
                    <FormGroup>
                      <Label for="name">Organization Name *</Label>
                      <Input
                        type="text"
                        id="name"
                        name="name"
                        placeholder="Enter organization name"
                        value={formik.values.name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={formik.touched.name && !!formik.errors.name}
                      />
                      {formik.touched.name && formik.errors.name && (
                        <div className="invalid-feedback">{formik.errors.name}</div>
                      )}
                      {flowData?.organizationName && formik.values.name === flowData.organizationName && (
                        <div className="form-text text-success">
                          <small><i className="fa-solid fa-circle-check me-1" />Pre-filled from your registration</small>
                        </div>
                      )}
                    </FormGroup>

                    <FormGroup>
                      <Label for="subdomain">Subdomain *</Label>
                      <div className="input-group subdomain-group">
                        <Input
                          type="text"
                          id="subdomain"
                          name="subdomain"
                          placeholder="your-org"
                          value={formik.values.subdomain}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          invalid={formik.touched.subdomain && !!formik.errors.subdomain}
                        />
                        <span className="input-group-text">.mentorgain.com</span>
                      </div>
                      {formik.touched.subdomain && formik.errors.subdomain && (
                        <div className="text-danger small mt-1">{formik.errors.subdomain}</div>
                      )}
                      {subdomainStatus === 'checking' && (
                        <div className="form-text text-muted">
                          <Spinner size="sm" className="me-1" /> Checking availability...
                        </div>
                      )}
                      {subdomainStatus === 'available' && (
                        <div className="form-text text-success">
                          <small><i className="fa-solid fa-circle-check me-1" />Subdomain is available</small>
                        </div>
                      )}
                      {subdomainStatus === 'unavailable' && (
                        <div className="form-text text-danger">
                          <small>
                            <i className="fa-solid fa-circle-xmark me-1" />
                            Subdomain is not available.
                            {subdomainSuggestion && <> Try: <strong>{subdomainSuggestion}</strong></>}
                          </small>
                        </div>
                      )}
                    </FormGroup>
                  </div>

                  {/* Section: Location & Time */}
                  <div className="form-section">
                    <div className="form-section-title">
                      <i className="fa-solid fa-globe" />
                      Location & Time
                    </div>
                    <FormGroup>
                      <Label for="timezone">Timezone *</Label>
                      <div className="timezone-filter">
                        <Input
                          type="text"
                          placeholder="Search timezones..."
                          value={timezoneFilter}
                          onChange={(e) => setTimezoneFilter(e.target.value)}
                        />
                      </div>
                      <Input
                        type="select"
                        id="timezone"
                        name="timezone"
                        value={formik.values.timezone}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={formik.touched.timezone && !!formik.errors.timezone}
                      >
                        {filteredTimezones.map((tz) => (
                          <option key={tz.value} value={tz.value}>
                            {tz.label}
                          </option>
                        ))}
                      </Input>
                      {formik.touched.timezone && formik.errors.timezone && (
                        <div className="invalid-feedback">{formik.errors.timezone}</div>
                      )}
                    </FormGroup>

                    <FormGroup>
                      <Label for="address">Address *</Label>
                      <Input
                        type="textarea"
                        id="address"
                        name="address"
                        rows={2}
                        placeholder="Enter your organization address"
                        value={formik.values.address}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={formik.touched.address && !!formik.errors.address}
                      />
                      {formik.touched.address && formik.errors.address && (
                        <div className="invalid-feedback">{formik.errors.address}</div>
                      )}
                    </FormGroup>
                  </div>

                  {/* Section: Contact Information */}
                  <div className="form-section">
                    <div className="form-section-title">
                      <i className="fa-solid fa-address-book" />
                      Contact Information
                    </div>
                    <Row>
                      <Col xs="12" md="6">
                        <FormGroup>
                          <Label for="email">Organization Email *</Label>
                          <Input
                            type="email"
                            id="email"
                            name="email"
                            placeholder="contact@yourcompany.com"
                            value={formik.values.email}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            invalid={formik.touched.email && !!formik.errors.email}
                          />
                          {formik.touched.email && formik.errors.email && (
                            <div className="invalid-feedback">{formik.errors.email}</div>
                          )}
                          {flowData?.email && formik.values.email === flowData.email && (
                            <div className="form-text text-success">
                              <small><i className="fa-solid fa-circle-check me-1" />Pre-filled from your registration</small>
                            </div>
                          )}
                        </FormGroup>
                      </Col>
                      <Col xs="12" md="6">
                        <FormGroup>
                          <Label for="phone">Phone Number *</Label>
                          <Input
                            type="tel"
                            id="phone"
                            name="phone"
                            placeholder="+1 (555) 123-4567"
                            value={formik.values.phone}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            invalid={formik.touched.phone && !!formik.errors.phone}
                          />
                          {formik.touched.phone && formik.errors.phone && (
                            <div className="invalid-feedback">{formik.errors.phone}</div>
                          )}
                        </FormGroup>
                      </Col>
                    </Row>
                  </div>

                  {/* Section: Tax Information (collapsible) */}
                  <div className="form-section">
                    {!showGst ? (
                      <button
                        type="button"
                        className="tax-toggle"
                        onClick={() => setShowGst(true)}
                      >
                        <i className="fa-solid fa-plus" />
                        Add tax information (optional)
                      </button>
                    ) : (
                      <>
                        <div className="form-section-title">
                          <i className="fa-solid fa-file-invoice" />
                          Tax Information (Optional)
                        </div>
                        <Row>
                          <Col xs="12" md="6">
                            <FormGroup className="mb-0">
                              <Label for="gst">GST / Tax ID</Label>
                              <Input
                                type="text"
                                id="gst"
                                name="gst"
                                placeholder="GST123456789"
                                value={formik.values.gst}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                              />
                              <div className="form-text text-muted">
                                <small>Tax identification number if applicable</small>
                              </div>
                            </FormGroup>
                          </Col>
                        </Row>
                      </>
                    )}
                  </div>

                  <div className="d-flex justify-content-end pt-3">
                    <Button
                      type="submit"
                      color="primary"
                      size="lg"
                      disabled={loading || !formik.isValid || subdomainStatus === 'unavailable'}
                    >
                      {loading ? <Spinner size="sm" className="me-2" /> : null}
                      Continue to Configuration
                    </Button>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateOrganization;
